(() => {
  const revealMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const revealColumns = window.matchMedia('(min-width: 801px)');
  const revealRecords = new Map();
  let revealObserver;
  let revealPrepareObserver;
  function prepareReveal(record, near) {
    record.near = near;
    record.targets.forEach(element => {
      if (near && !revealColumns.matches && !revealMotion.matches) element.style.willChange = 'transform, opacity';
      else element.style.removeProperty('will-change');
    });
  }
  function inRevealViewport(element) {
    const box = element.getBoundingClientRect();
    return box.bottom > 0 && box.top < window.innerHeight * (revealColumns.matches ? 0.92 : 1) && box.right > 0 && box.left < window.innerWidth;
  }
  function stopReveal(record) {
    record.run += 1;
    record.animations.forEach(animation => animation.cancel());
    record.animations = [];
    record.playing = false;
    record.targets.forEach(element => { element.dataset.motionState = 'visible'; });
    prepareReveal(record, false);
  }
  function playReveal(record) {
    if (record.playing || revealMotion.matches || record.trigger.contains(document.activeElement)) return;
    record.playing = true;
    const run = ++record.run;
    record.animations = record.targets.map(element => {
      const mobile = !revealColumns.matches;
      const direction = element.dataset.motionDirection;
      const horizontal = mobile ? Math.min(132, window.innerWidth * 0.28) : Math.min(120, window.innerWidth * 0.12);
      const vertical = Math.min(80, window.innerHeight * 0.1);
      const x = direction === 'left' ? -horizontal : direction === 'right' ? horizontal : 0;
      const y = mobile ? 0 : direction === 'top' ? -vertical : direction === 'bottom' ? vertical : 0;
      const isImage = element.matches('figure, .photo-frame');
      element.dataset.motionState = 'running';
      element.dataset.motionPlays = String(Number(element.dataset.motionPlays || 0) + 1);
      return element.animate([
        { opacity: mobile ? 0.35 : 0.08, transform: `translate3d(${x}px, ${y}px, 0)` },
        { opacity: 1, transform: 'translate3d(0, 0, 0)' }
      ], { duration: mobile ? (isImage ? 1100 : 1000) : (isImage ? 1400 : 1250), easing: mobile ? 'cubic-bezier(.22,.61,.36,1)' : 'cubic-bezier(.25,.46,.45,.94)' });
    });
    Promise.allSettled(record.animations.map(animation => animation.finished)).then(() => {
      if (record.run !== run) return;
      record.playing = false;
      record.animations = [];
      record.inside = inRevealViewport(record.trigger);
      record.targets.forEach(element => { element.dataset.motionState = 'visible'; });
    });
  }
  function initReveals(root) {
    if (!('IntersectionObserver' in window) || !('animate' in Element.prototype)) return;
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const record = revealRecords.get(entry.target);
          if (!record) return;
          if (entry.isIntersecting) {
            if (!record.inside) {
              record.inside = true;
              playReveal(record);
            }
          } else if (!record.playing) {
            record.inside = false;
          }
        });
      }, { threshold: 0, rootMargin: revealColumns.matches ? '0px 0px -8% 0px' : '0px' });
      if (!revealColumns.matches) {
        revealPrepareObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            const record = revealRecords.get(entry.target);
            if (record) prepareReveal(record, entry.isIntersecting);
          });
        }, { threshold: 0, rootMargin: '180px 0px' });
      }
    }
    revealRecords.forEach((record, trigger) => {
      if (!trigger.isConnected) {
        stopReveal(record);
        revealObserver.unobserve(trigger);
        if (revealPrepareObserver) revealPrepareObserver.unobserve(trigger);
        revealRecords.delete(trigger);
      }
    });
    const added = new Set();
    root.querySelectorAll('[data-motion]').forEach(element => {
      const scope = element.dataset.motionScope;
      if ((scope === 'mobile' && revealColumns.matches) || (scope === 'desktop' && !revealColumns.matches)) return;
      if (element.dataset.motionReady) return;
      element.dataset.motionReady = 'true';
      const direction = element.dataset.motion;
      element.dataset.motionDirection = !revealColumns.matches ? ({ top: 'left', bottom: 'right' }[direction] || direction) : direction;
      const trigger = (revealColumns.matches && element.closest('[data-motion-trigger]')) || element;
      let record = revealRecords.get(trigger);
      if (!record) {
        record = { trigger, targets: [], inside: false, playing: false, animations: [], run: 0 };
        revealRecords.set(trigger, record);
        added.add(record);
      }
      record.targets.push(element);
    });
    added.forEach(record => {
      revealObserver.observe(record.trigger);
      if (revealPrepareObserver) revealPrepareObserver.observe(record.trigger);
      if (inRevealViewport(record.trigger)) {
        record.inside = true;
        playReveal(record);
      }
    });
  }
  revealMotion.addEventListener('change', () => {
    if (revealMotion.matches) revealRecords.forEach(stopReveal);
  });
  revealColumns.addEventListener('change', () => {
    if (!revealObserver) return;
    revealRecords.forEach(record => {
      stopReveal(record);
      record.targets.forEach(element => { delete element.dataset.motionReady; });
    });
    revealObserver.disconnect();
    if (revealPrepareObserver) revealPrepareObserver.disconnect();
    revealObserver = undefined;
    revealPrepareObserver = undefined;
    revealRecords.clear();
    initReveals(document);
  });
  document.addEventListener('focusin', event => {
    revealRecords.forEach(record => {
      if (record.playing && record.trigger.contains(event.target)) stopReveal(record);
    });
  });
  document.addEventListener('shopify:section:unload', event => {
    revealRecords.forEach((record, trigger) => {
      if (event.target.contains(trigger)) {
        stopReveal(record);
        revealObserver.unobserve(trigger);
        if (revealPrepareObserver) revealPrepareObserver.unobserve(trigger);
        revealRecords.delete(trigger);
      }
    });
  });
  function init(root = document) {
    root.querySelectorAll('.site-header').forEach(header => {
      if (header.dataset.measured) return;
      header.dataset.measured = 'true';
      const update = () => document.documentElement.style.setProperty('--header-height', `${header.getBoundingClientRect().height}px`);
      update();
      new ResizeObserver(update).observe(header);
    });
    root.querySelectorAll('[data-menu-toggle]').forEach(toggle => {
      if (toggle.dataset.ready) return;
      toggle.dataset.ready = 'true';
      const nav = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!nav) return;
      function close() { nav.classList.remove('is-open'); toggle.setAttribute('aria-expanded', 'false'); toggle.setAttribute('aria-label', 'Abrir menú'); }
      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        nav.classList.toggle('is-open', open); toggle.setAttribute('aria-expanded', String(open));
        toggle.setAttribute('aria-label', open ? 'Cerrar menú' : 'Abrir menú');
      });
      nav.querySelectorAll('a').forEach(link => link.addEventListener('click', close));
      document.addEventListener('keydown', event => { if (event.key === 'Escape') close(); });
    });
    root.querySelectorAll('[data-open-dialog]').forEach(button => {
      if (button.dataset.ready) return;
      button.dataset.ready = 'true';
      button.addEventListener('click', () => {
        const dialog = document.getElementById(button.dataset.openDialog);
        if (dialog && !dialog.open) dialog.showModal();
      });
    });
    root.querySelectorAll('dialog').forEach(dialog => {
      if (dialog.dataset.ready) return;
      dialog.dataset.ready = 'true';
      dialog.querySelectorAll('[data-close-dialog]').forEach(button => button.addEventListener('click', () => dialog.close()));
      dialog.addEventListener('click', event => { if (event.target === dialog) {
        const box = dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
      }});
    });
    initReveals(root);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
